import React, { useMemo, useCallback, useState, useRef } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import Checkbox from '@adsk/alloy-react-checkbox';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressBar from '@adsk/alloy-react-progress-bar';
import StatusColorIndicator from '@adsk/alloy-react-status-color-indicator';
import {
  LoadMoreDataRow,
  useTable,
  TableHeaderGroup,
  TableHeader,
  TableRow,
  TableCell,
} from '@adsk/alloy-react-table';
import Theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import {
  getStatus,
  STATUS_COLOR_MAPPING,
  COST_TYPE,
  shouldShowStatus,
  MAPPING_KEY,
} from '../helpers/consts';
import { getDeepLink } from '../helpers/formatEntities';

const StyledScrollContainer = styled.div`
  height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledStickyThead = styled.thead`
  position: sticky;
  margin: 0;
  top: 0;
  z-index: 1;
`;

const CustomDataView = ({
  disabledIds,
  entities,
  selectedIds,
  filters: { searchExpression },
  onLoadMore,
  multiple,
  error,
  setSelection,
  onTryAgain,
  type = '',
}) => {
  const project = useCurrentProject();
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  const rowSelection = useMemo(() => {
    const rowSelection = {};
    for (const id of selectedIds) {
      rowSelection[id] = true;
    }
    return rowSelection;
  }, [selectedIds]);

  const [loading, setLoading] = useState(false);
  const dataTable = useRef(null);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        size: 52,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : table.getIsAllRowsSelected()
            }
            onChange={(_val, e) => {
              const toggleAll = table.getToggleAllRowsSelectedHandler();
              toggleAll?.(e);
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={
              row.getIsSomeSelected() ? 'indeterminate' : row.getIsSelected()
            }
            disabled={!row.getCanSelect() || disabledIds.includes(row.id)}
          />
        ),
      },
      {
        id: MAPPING_KEY[type] || T.translate('CODE'),
        size: shouldShowStatus(type) ? 150 : 300,
        minWidth: 150,
        header: () => T.translate(MAPPING_KEY[type]),
        cell: ({ row }) => (
          <A
            target="__blank"
            href={getDeepLink(row.original, project, type)}
            style={{ width: '100% !important', pointEvents: 'all !important' }}
          >
            <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
              <HighlightText
                style={{ color: Theme.colors.adskBlue500 }}
                matched={searchExpression}
                text={row.original[MAPPING_KEY[type]]}
              />
            </OverflowTooltip>
          </A>
        ),
      },
      {
        id: 'name',
        size: 300,
        minWidth: 200,
        header: () => T.translate('NAME'),
        cell: ({ row }) => (
          <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
            <HighlightText
              matched={searchExpression}
              text={row.original.name}
            />
          </OverflowTooltip>
        ),
      },
      ...(shouldShowStatus(type)
        ? [
            {
              id: 'status',
              accessor: 'status',
              size: 200,
              minWidth: 150,
              header: () => T.translate('STATUS'),
              cell: ({ row }) => (
                <OverflowTooltip>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StatusColorIndicator
                      statusColor={
                        STATUS_COLOR_MAPPING[getStatus(type, row.original)]
                      }
                      style={{
                        height: '16px',
                        width: '4px',
                        cursor: 'default',
                      }}
                    />
                    <span style={{ marginLeft: '5px' }}>
                      <HighlightText
                        matched={searchExpression}
                        text={
                          getStatus(type, row.original)
                            ? T.translate(
                                getStatus(type, row.original).toUpperCase(),
                              )
                            : null
                        }
                      />
                    </span>
                  </div>
                </OverflowTooltip>
              ),
            },
          ]
        : []),
    ],
    [T, disabledIds, project, searchExpression, type],
  );

  const table = useReactTable({
    data: entities,
    getRowId: (row) => row.id,
    columns,
    enableRowSelection: (row) => !disabledIds.includes[row.id],
    enableMultiRowSelection: multiple,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    onRowSelectionChange: (updaterOrValue) => {
      const newValue =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      setSelection(
        entities.filter(({ id }) => newValue[id] && !disabledIds.includes(id)),
        false,
      );
    },
  });

  const {
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable({ table, enableMultiRowRangeSelection: true });

  const onScroll = useCallback(async () => {
    if (dataTable.current) {
      const tableElement = dataTable.current;
      // avoid loading data when has error or already in loading
      if (
        !loading &&
        !error &&
        onLoadMore &&
        Math.round(tableElement.scrollTop) ===
          Math.round(tableElement?.scrollHeight - tableElement?.offsetHeight)
      ) {
        setLoading(true);
        await onLoadMore();
        setLoading(false);
      }
    }
  }, [onLoadMore, error, loading, dataTable]);

  return (
    <StyledScrollContainer onScroll={onScroll} ref={dataTable}>
      <table
        style={{
          borderSpacing: 0,
          width: '100%',
        }}
      >
        <StyledStickyThead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderGroup
              key={headerGroup.id}
              {...getTableHeaderGroupProps(headerGroup)}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} {...getTableHeaderProps(header)} />
              ))}
            </TableHeaderGroup>
          ))}
        </StyledStickyThead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              id={row.id}
              disabled={disabledIds.includes(row.id)}
              {...getTableRowProps(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} {...getTableCellProps(cell)} />
              ))}
            </TableRow>
          ))}
          {!!error && onLoadMore && (
            <LoadMoreDataRow
              hasError={!!error}
              onRetry={async () => onTryAgain()}
              onLoad={
                onLoadMore
                  ? async () => onLoadMore()
                  : error
                    ? async () => onTryAgain()
                    : undefined
              }
            />
          )}
        </tbody>
      </table>
      {/* marginTop is needed to avoid to trigger onScroll when reaching bottom */}
      {loading && !error && <ProgressBar style={{ marginTop: '-4px' }} />}
    </StyledScrollContainer>
  );
};

CustomDataView.displayName = 'CustomDataView';
CustomDataView.defaultProps = {
  filters: {},
  selectedIds: [],
  disabledIds: [],
  entities: [],
};

CustomDataView.propTypes = {
  /**
   * Whether had error fetching PCO list from BE.
   */
  error: PropTypes.bool,
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * Load the next page of PCO entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of selected PCO entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of PCO entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of entities.
   */
  entities: PropTypes.arrayOf(
    PropTypes.PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      code: PropTypes.string,
      number: PropTypes.string,
      costStatus: PropTypes.string,
      budgetStatus: PropTypes.string,
    }),
  ),
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * Callback to change selection.
   */
  onChange: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * The cost type, should be one of 'pco', 'oco', 'sco', 'rfq', 'rco','budget'
   */
  type: PropTypes.oneOf(COST_TYPE).isRequired,
};

export default CustomDataView;
